import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Grid, Menu, Tooltip } from 'antd';
import { MoreHorizontal } from 'lucide-react';

// =============================================================================
// PageHeaderActions — v2 React primitive for the `rightContent` slot of
// `PageHeaderLayout`. Encapsulates the responsive collapse logic that
// Campaign Details proved: above `collapseAt` everything is inline, below
// it everything collapses into a `⋯` overflow menu except the primary
// action and any passthrough children (e.g. AmplifyView).
//
// Rendering order at wide (≥ collapseAt):
//   primary · secondary[] · destructive · children · iconActions[] · ⋯
//
// Rendering at compact (< collapseAt):
//   primary · children · ⋯(containing secondary + destructive + iconActions)
//
// Consumer contract — see SPECIFICATION.md §10 "`.page-header-actions`
// consumer contract". Action objects shape:
//   { label, icon, onClick, tooltip?, danger?, disabled?, key? }
//
// Uses plain AntD `Button` directly (imported from `'antd'`, not the
// legacy `shared/globalStyling/Button` wrapper which ships inline styles
// that defeat v2 chrome — see CHANGELOG "Campaign Details header buttons
// forced rectangular by GlobalButton wrapper").
// =============================================================================

const renderInlineAction = (action) => {
  const { key, label, icon, onClick, tooltip, disabled, danger, variant } = action;
  const btnProps = {
    onClick,
    icon,
    disabled,
  };
  if (variant === 'primary') {
    btnProps.type = 'primary';
  } else if (variant === 'destructive' || danger) {
    btnProps.type = 'danger';
  }
  const button = (
    <Button key={key} {...btnProps}>
      {label}
    </Button>
  );
  return tooltip ? (
    <Tooltip key={key} title={tooltip}>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

const renderInlineIconAction = (action) => {
  const { key, label, icon, onClick, tooltip, disabled } = action;
  const button = (
    <Button
      key={key}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    />
  );
  return tooltip ? (
    <Tooltip key={key} title={tooltip}>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

const PageHeaderActions = ({
  primary,
  secondary = [],
  destructive,
  iconActions = [],
  children,
  collapseAt = 'xl',
  className,
}) => {
  const screens = Grid.useBreakpoint();
  const isCompact = !screens[collapseAt];

  // Assemble overflow actions for compact tier — order mirrors visual
  // priority at wide (secondaries → destructive → iconActions).
  const overflowActions = [];
  if (isCompact) {
    secondary.forEach((a) => overflowActions.push(a));
    if (destructive) overflowActions.push({ ...destructive, danger: true });
    iconActions.forEach((a) => overflowActions.push(a));
  }

  const extraClass = className ? ` ${className}` : '';

  return (
    <div className={`page-header-actions${extraClass}`}>
      {primary && renderInlineAction({ ...primary, variant: 'primary' })}

      {!isCompact &&
        secondary.map((action) =>
          renderInlineAction({ ...action, key: action.key || action.label })
        )}

      {!isCompact &&
        destructive &&
        renderInlineAction({
          ...destructive,
          variant: 'destructive',
          key: destructive.key || 'destructive',
        })}

      {children}

      {!isCompact &&
        iconActions.map((action) =>
          renderInlineIconAction({ ...action, key: action.key || action.label })
        )}

      {overflowActions.length > 0 && (
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          overlayClassName="mjl-ds"
          overlay={
            <Menu>
              {overflowActions.map((a) => (
                <Menu.Item
                  key={a.key || a.label}
                  icon={a.icon}
                  onClick={a.onClick}
                  danger={a.danger}
                  disabled={a.disabled}
                >
                  {a.label}
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button
            className="page-header-actions__overflow"
            icon={<MoreHorizontal size={16} strokeWidth={2} />}
            aria-label="More actions"
          />
        </Dropdown>
      )}
    </div>
  );
};

const actionShape = PropTypes.shape({
  key: PropTypes.string,
  label: PropTypes.node.isRequired,
  icon: PropTypes.node,
  onClick: PropTypes.func.isRequired,
  tooltip: PropTypes.node,
  disabled: PropTypes.bool,
});

PageHeaderActions.propTypes = {
  primary: actionShape,
  secondary: PropTypes.arrayOf(actionShape),
  destructive: actionShape,
  iconActions: PropTypes.arrayOf(actionShape),
  children: PropTypes.node,
  collapseAt: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  className: PropTypes.string,
};

export default PageHeaderActions;
